/**
 * Per-type cell editors, populated from the board's real column settings (REQ-6.2):
 * status/priority use the board's labels in the board's colours, people list real
 * users, dropdowns list the real options — the user is never asked to type a raw
 * value monday would reject (REQ-6.3).
 *
 * Draft value conventions are those of lib/columnValues.js.
 */
import { ChevronDown, Loader2 } from 'lucide-react';
import { Button, Checkbox, DatePicker, RadioButton, Search, Tab, TabList, TextField } from 'monday-ui-react-core';
import { CloseSmall } from 'monday-ui-react-core/icons';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { canonicalType } from '../../Config';
import { mentionsMissingScope, PERMISSION_HINT } from '../../lib/captureGroup';
import { isValidLinkUrl } from '../../lib/columnValues';
import {
  DEFAULT_TIMELINE_RULE,
  DYNAMIC_PRESETS,
  MAX_OFFSET,
  TIMELINE_PRESETS,
  addDays,
  applyBaseDate,
  formatDate,
  resolveDate,
  resolveTimeline,
  timelinePreset,
} from '../../lib/dynamicValues';
import { RatingSymbol, ratingSettings } from '../RatingSymbols';
import AnchoredPopover from './AnchoredPopover';
import FileCellEditor from './FileCellEditor';

function StatusEditor({ column, value, onChange, onClose }) {
  const labels = (column.settings && column.settings.labels) || [];
  if (!labels.length) {
    return (
      <div className="spt-menu-empty">
        This column is not on the current board, so its labels are unknown here. Values can
        still be kept or cleared.
      </div>
    );
  }
  return (
    <>
      <div className="spt-statuslist">
        {labels.map((l) => (
          <button
            key={l.index}
            type="button"
            className={`spt-statusopt ${value && value.index === l.index ? 'spt-statusopt-on' : ''}`}
            style={{ backgroundColor: l.color }}
            onClick={() => {
              onChange({ index: l.index, label: l.label });
              onClose();
            }}
          >
            {l.label}
          </button>
        ))}
      </div>
      <Button
        kind={Button.kinds.TERTIARY}
        size={Button.sizes.SMALL}
        leftIcon={CloseSmall}
        onClick={() => {
          onChange(null);
          onClose();
        }}
      >
        Clear
      </Button>
    </>
  );
}

function PeopleEditor({ users, usersError, usersLoading, onNeedUsers, value, onChange }) {
  const [query, setQuery] = useState(''); // hooks must run before any early return
  // Members are fetched lazily — the first People cell opened asks for them, and
  // the loader ignores the call when they are already loading or in hand.
  useEffect(() => {
    if (onNeedUsers) onNeedUsers();
  }, [onNeedUsers]);
  if (usersLoading) {
    return <div className="spt-menu-empty">Loading board members…</div>;
  }
  if (!users || !users.length) {
    // An API failure and a genuinely empty board look identical in the list, so
    // say which one it is — monday returns scope errors as HTTP 200 + errors[],
    // and "no members" was hiding them.
    return usersError ? (
      <div className="spt-menu-empty">
        {mentionsMissingScope(usersError)
          ? `Could not load this board's members: this version of the app needs a permission that hasn’t been approved yet. ${PERMISSION_HINT}`
          : `Could not load this board's members. monday said: “${usersError}”.`}
      </div>
    ) : (
      <div className="spt-menu-empty">
        No members found on this board. The picker only offers people who are
        subscribed to the board, since only they can be assigned. Add them to the
        board first, then reopen the editor.
      </div>
    );
  }
  const selected = (value && value.persons) || [];
  const isOn = (id) => selected.some((p) => String(p.id) === String(id));
  const toggle = (u) => {
    const next = isOn(u.id)
      ? selected.filter((p) => String(p.id) !== String(u.id))
      : [...selected, { id: u.id, name: u.name }];
    onChange(next.length ? { persons: next } : null);
  };
  const list = users.filter(
    (u) => !query.trim() || u.name.toLowerCase().includes(query.trim().toLowerCase()),
  );
  return (
    <>
      <Search size={Search.sizes.SMALL} placeholder="Search names" value={query} onChange={setQuery} autoFocus />
      <div className="spt-menu-list">
        {list.map((u) => (
          <div key={u.id} className="spt-menu-row spt-menu-row-check">
            <Checkbox checked={isOn(u.id)} onChange={() => toggle(u)} label={u.name} />
          </div>
        ))}
        {list.length === 0 && <div className="spt-menu-empty">No matching members</div>}
      </div>
    </>
  );
}

function DropdownEditor({ column, value, onChange }) {
  const options = (column.settings && column.settings.options) || [];
  const selected = (value && value.labels) || [];
  const selectedIds = (value && value.ids) || [];
  const toggle = (opt) => {
    const on = selected.includes(opt.name);
    const labels = on ? selected.filter((l) => l !== opt.name) : [...selected, opt.name];
    const ids = on
      ? selectedIds.filter((i) => i !== opt.id)
      : [...selectedIds, opt.id].filter((i) => i !== undefined);
    onChange(labels.length ? { ids, labels } : null);
  };
  if (!options.length) {
    return (
      <div className="spt-menu-empty">
        This column is not on the current board, so its options are unknown here.
      </div>
    );
  }
  return (
    <div className="spt-menu-list">
      {options.map((opt) => (
        <div key={opt.id ?? opt.name} className="spt-menu-row spt-menu-row-check">
          <Checkbox checked={selected.includes(opt.name)} onChange={() => toggle(opt)} label={opt.name} />
        </div>
      ))}
    </div>
  );
}

// Picked dates use monday's calendar (Vibe DatePicker) rather than the browser's
// date field, whose text follows the OS region ("dd/mm/yyyy") and so never
// matched the cells. The calendar has no text; the line under it and the cells
// read "Oct 23, 2026" (formatDate).
const ISO = 'YYYY-MM-DD';
/** 'YYYY-MM-DD' → the calendar's moment, at local midnight so the day never shifts; else undefined. */
const toMoment = (iso) => {
  const m = iso ? moment(iso, ISO, true) : null;
  return m && m.isValid() ? m : undefined;
};
const fromMoment = (m) => (m && m.isValid() ? m.format(ISO) : '');

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const weekdayOf = (iso) => WEEKDAYS[new Date(`${iso}T00:00:00Z`).getUTCDay()];
const clampOffset = (n) => Math.max(-MAX_OFFSET, Math.min(MAX_OFFSET, n));
/** What was typed as a whole number ("-3", "05"), or null: '' (also what a number field reports for "1."), "1.5". */
const wholeNumber = (raw) => (/^-?\d+$/.test(String(raw).trim()) ? Number(raw) : null);

/** A native select drawn like monday's dropdown field: 32px, hairline edge, chevron. */
function SelectField({ label, value, onChange, children }) {
  return (
    <span className="spt-select">
      <select className="spt-field" value={value} aria-label={label} onChange={(e) => onChange(e.target.value)}>
        {children}
      </select>
      <ChevronDown className="spt-select-chevron" aria-hidden="true" />
    </span>
  );
}

/**
 * "[+3] [days | working days]": the signed offset and unit every dynamic date
 * row shares. The number field's own stepper goes below zero; a positive
 * offset shows its "+" inside the field (a number input can't hold one).
 */
function OffsetFields({ label, offset, unit, min = -MAX_OFFSET, onChange }) {
  // What was typed stays in the field; only a whole number becomes the offset,
  // anything else (empty, "1.5") makes it null, so the editor says so and Set
  // waits. A number from outside (or clamped to the limit) replaces the text.
  const [typed, setTyped] = useState(null);
  const shown = typed !== null && (offset === null || wholeNumber(typed) === offset) ? typed : offset === null ? '' : String(offset);
  return (
    <>
      <span className="spt-field spt-numfield">
        {offset > 0 && <span aria-hidden="true">+</span>}
        <input
          type="number"
          min={min}
          max={MAX_OFFSET}
          step={1}
          value={shown}
          aria-label={`${label} offset`}
          aria-invalid={offset === null || undefined}
          onFocus={() => onChange({ offset, unit })}
          onChange={(e) => {
            const n = wholeNumber(e.target.value);
            setTyped(e.target.value);
            onChange({ offset: n === null ? null : clampOffset(n), unit });
          }}
        />
      </span>
      <SelectField label={`${label} unit`} value={unit} onChange={(u) => onChange({ offset, unit: u })}>
        <option value="days">days</option>
        <option value="working_days">working days</option>
      </SelectField>
    </>
  );
}

/** monday's own tabs over the date and timeline editors. */
function DateTabs({ tab, setTab, pickLabel }) {
  return (
    <TabList
      className="spt-dateeditor-tabs"
      size="sm"
      activeTabId={tab === 'pick' ? 0 : 1}
      onTabChange={(i) => setTab(i === 0 ? 'pick' : 'dynamic')}
    >
      <Tab>{pickLabel}</Tab>
      <Tab>Dynamic</Tab>
    </TabList>
  );
}

const WEEK_HEAD = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const MAX_STRIP_WEEKS = 6;
const mondayIndex = (iso) => (new Date(`${iso}T00:00:00Z`).getUTCDay() + 6) % 7;
const daysBetween = (a, b) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400000);

/**
 * Where a rule lands if applied today, as Monday-first weeks: the landed days
 * filled, today outlined, weekends muted — so "working days" skipping a
 * weekend is visible at a glance. The weeks run from whichever of today and
 * the range comes first to whichever comes last; past six weeks only the
 * first and last two are drawn, with the gap counted between them.
 */
function WeekStrip({ from, to, today, caption }) {
  const lo = [from, today].sort()[0];
  const hi = [to, today].sort()[1];
  const first = addDays(lo, -mondayIndex(lo));
  const count = Math.floor(daysBetween(first, hi) / 7) + 1;
  const week = (w) => Array.from({ length: 7 }, (_, d) => addDays(first, w * 7 + d));
  const all = Array.from({ length: count }, (_, w) => w);
  const shown = count > MAX_STRIP_WEEKS ? [0, 1, null, count - 2, count - 1] : all;
  return (
    <div className="spt-weekstrip" role="img" aria-label={caption}>
      {WEEK_HEAD.map((h, i) => (
        <span key={`h${i}`} className={`spt-weekstrip-head${i > 4 ? ' is-weekend' : ''}`}>
          {h}
        </span>
      ))}
      {shown.map((w) =>
        w === null ? (
          <span key="gap" className="spt-weekstrip-gap">
            ⋯ {count - 4} more weeks
          </span>
        ) : (
          week(w).map((iso, d) => {
            const cls = ['spt-weekstrip-day'];
            if (d > 4) cls.push('is-weekend');
            if (iso >= from && iso <= to) cls.push('is-on');
            if (iso === today) cls.push('is-today');
            return (
              <span key={iso} className={cls.join(' ')} title={`${weekdayOf(iso)} ${formatDate(iso)}`}>
                {Number(iso.slice(8))}
              </span>
            );
          })
        ),
      )}
    </div>
  );
}

/** Where the rule lands today (a caption and a week strip), then Clear and Set. */
function DynamicFooter({ lands, today, invalid, canSet, onClear, onSet }) {
  const caption = lands
    ? lands.from === lands.to
      ? `Preview: ${weekdayOf(lands.from)} ${formatDate(lands.from)}`
      : `Preview: ${formatDate(lands.from).replace(/, \d{4}$/, '')} – ${formatDate(lands.to)}`
    : invalid;
  return (
    <div className="spt-dateeditor-footer">
      <p className="spt-dateeditor-preview">{caption}</p>
      {lands && <WeekStrip from={lands.from} to={lands.to} today={today} caption={caption} />}
      <div className="flex justify-end gap-2">
        <Button kind={Button.kinds.TERTIARY} size={Button.sizes.SMALL} onClick={onClear}>
          Clear
        </Button>
        <Button size={Button.sizes.SMALL} disabled={!canSet} onClick={onSet}>
          Set
        </Button>
      </div>
    </div>
  );
}

/**
 * A date cell holds either a fixed date ('YYYY-MM-DD') or a rule that resolves
 * when the template is applied ({ dynamic: { base, offset, unit } }, see
 * lib/dynamicValues). Two tabs: Pick a date, and Dynamic with Today / Tomorrow /
 * Yesterday or "Today [+n] [days | working days]", plus a preview of where the
 * rule lands today. "Today" is the apply date.
 */
function DateEditor({ value, onChange, onClose }) {
  const isDynamic = Boolean(value && typeof value === 'object' && value.dynamic);
  const [tab, setTab] = useState(isDynamic ? 'dynamic' : 'pick');
  const [rule, setRule] = useState(isDynamic ? value.dynamic : DYNAMIC_PRESETS[0].rule);
  const isPreset = (r) => DYNAMIC_PRESETS.some((p) => p.rule.offset === r.offset && p.rule.unit === r.unit);
  // the custom row keeps its own number and unit so picking a preset never loses them
  const [custom, setCustom] = useState(!isPreset(rule) ? { offset: rule.offset, unit: rule.unit } : { offset: 5, unit: 'days' });

  const today = applyBaseDate(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const lands = resolveDate(rule, today);
  const selectedKey = isPreset(rule)
    ? DYNAMIC_PRESETS.find((p) => p.rule.offset === rule.offset && p.rule.unit === rule.unit).key
    : 'custom';

  const pickPreset = (p) => setRule(p.rule);
  const pickCustom = (c) => {
    setCustom(c);
    setRule({ base: 'apply', ...c });
  };
  const clear = () => {
    onChange(null);
    onClose();
  };
  const picked = typeof value === 'string' ? value : '';

  return (
    <div className="spt-dateeditor">
      <DateTabs tab={tab} setTab={setTab} pickLabel="Pick a date" />

      {tab === 'pick' ? (
        <>
          <DatePicker date={toMoment(picked)} onPickDate={(m) => onChange(fromMoment(m) || null)} />
          <div className="flex items-center justify-between">
            <p className="spt-dateeditor-preview">{picked ? formatDate(picked) : 'No date picked'}</p>
            <Button kind={Button.kinds.TERTIARY} size={Button.sizes.SMALL} leftIcon={CloseSmall} onClick={clear}>
              Clear
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="spt-dateeditor-options" role="radiogroup" aria-label="Dynamic date">
            {DYNAMIC_PRESETS.map((p) => (
              <RadioButton key={p.key} name="spt-dyn" text={p.label} checked={selectedKey === p.key} onSelect={() => pickPreset(p)} />
            ))}
            <div className="spt-dateeditor-row">
              <RadioButton name="spt-dyn" text="Today" checked={selectedKey === 'custom'} onSelect={() => pickCustom(custom)} />
              <OffsetFields label="Date" offset={custom.offset} unit={custom.unit} onChange={pickCustom} />
            </div>
          </div>
          <DynamicFooter
            lands={lands ? { from: lands, to: lands } : null}
            today={today}
            invalid="Choose a whole number of days."
            canSet={Boolean(lands)}
            onClear={clear}
            onSet={() => {
              onChange({ dynamic: rule });
              onClose();
            }}
          />
        </>
      )}
    </div>
  );
}

/**
 * A timeline holds either two fixed dates or a rule for each end, resolved when
 * the template is applied (lib/dynamicValues). The Dynamic tab offers Next week
 * and Next month (Today → +7 / +30 days), or Custom, which gives each end the
 * date column's own control — a base, a signed number and days / working days:
 *   Start  Today            [0]  [days]
 *   End    [Start | Today]  [+4] [working days]
 */
function TimerangeEditor({ value, onChange, onClose }) {
  const isDynamic = Boolean(value && typeof value === 'object' && value.dynamic);
  const [tab, setTab] = useState(isDynamic ? 'dynamic' : 'pick');
  const [rule, setRule] = useState(() => {
    const r = isDynamic ? value.dynamic : DEFAULT_TIMELINE_RULE;
    return { start: { ...r.start }, end: { ...r.end } };
  });
  // a stored custom rule that happens to equal a preset still opens as that preset
  const [mode, setMode] = useState(() => (isDynamic && timelinePreset(value.dynamic) ? timelinePreset(value.dynamic).key : 'custom'));
  // Custom keeps its own rule so trying a preset never loses it
  const [custom, setCustom] = useState(rule);
  // The dates as picked: the calendar's first click starts a range, the second
  // ends it on or after the start. Only a whole range is handed on, so the cell
  // keeps its last range until the new one has an end. A range that ends before
  // it starts (stored before the calendar) says so.
  const [range, setRange] = useState(() => (!isDynamic && value ? value : { from: '', to: '' }));
  const backwards = Boolean(range.from && range.to && range.from > range.to);
  const pickRange = ({ startDate, endDate }) => {
    const next = { from: fromMoment(startDate), to: fromMoment(endDate) };
    setRange(next);
    if (next.from && next.to && next.from <= next.to) onChange(next);
  };
  const clearRange = () => {
    onChange(null);
    onClose();
  };

  const today = applyBaseDate(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const lands = resolveTimeline(rule, today);
  const pickPreset = (p) => {
    setMode(p.key);
    setRule({ start: { ...p.rule.start }, end: { ...p.rule.end } });
  };
  const pickCustom = () => {
    setMode('custom');
    setRule(custom);
  };
  const setEnd = (which, patch) => {
    const next = { ...custom, [which]: { ...custom[which], ...patch } };
    setCustom(next);
    setRule(next);
  };

  return (
    <div className="spt-dateeditor">
      <DateTabs tab={tab} setTab={setTab} pickLabel="Pick dates" />

      {tab === 'pick' ? (
        <div>
          <DatePicker range date={toMoment(range.from)} endDate={toMoment(range.to)} onPickDate={pickRange} />
          <p className="spt-dateeditor-preview">
            {!range.from
              ? 'Pick a start date, then an end date'
              : `${formatDate(range.from)} – ${range.to ? formatDate(range.to) : 'pick an end date'}`}
          </p>
          {backwards && (
            <p className="mt-1 text-xs text-danger-ink" role="alert">
              The end date can’t be before the start date.
            </p>
          )}
          <div className="flex justify-end">
            <Button kind={Button.kinds.TERTIARY} size={Button.sizes.SMALL} leftIcon={CloseSmall} onClick={clearRange}>
              Clear
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="spt-dateeditor-options" role="radiogroup" aria-label="Dynamic timeline">
            {TIMELINE_PRESETS.map((p) => (
              <RadioButton key={p.key} name="spt-dyn-tl" text={p.label} checked={mode === p.key} onSelect={() => pickPreset(p)} />
            ))}
            <RadioButton name="spt-dyn-tl" text="Custom" checked={mode === 'custom'} onSelect={pickCustom} />
            {mode === 'custom' && (
              <div className="spt-dateeditor-grid">
                <span className="spt-dateeditor-key">Start</span>
                <span className="spt-dateeditor-row">
                  <span className="spt-dateeditor-base">Today</span>
                  <OffsetFields label="Start" offset={custom.start.offset} unit={custom.start.unit} onChange={(p) => setEnd('start', p)} />
                </span>
                <span className="spt-dateeditor-key">End</span>
                <span className="spt-dateeditor-row">
                  <SelectField
                    label="End counts from"
                    value={custom.end.base}
                    onChange={(base) =>
                      setEnd('end', { base, offset: base === 'start' && custom.end.offset !== null ? Math.max(0, custom.end.offset) : custom.end.offset })
                    }
                  >
                    <option value="start">Start</option>
                    <option value="apply">Today</option>
                  </SelectField>
                  <OffsetFields
                    label="End"
                    offset={custom.end.offset}
                    unit={custom.end.unit}
                    min={custom.end.base === 'start' ? 0 : -MAX_OFFSET}
                    onChange={(p) => setEnd('end', p)}
                  />
                </span>
              </div>
            )}
          </div>
          <DynamicFooter
            lands={lands}
            today={today}
            invalid="Choose whole numbers of days."
            canSet={Boolean(lands)}
            onClear={() => {
              onChange(null);
              onClose();
            }}
            onSet={() => {
              onChange({ dynamic: rule });
              onClose();
            }}
          />
        </>
      )}
    </div>
  );
}

function LinkEditor({ value, onChange }) {
  // The link as typed. A URL that isn't a web address stays on screen with a
  // message, and is never handed on: the cell keeps its last good link.
  const [v, setV] = useState(() => value || { url: '', text: '' });
  const badUrl = Boolean(v.url && v.url.trim()) && !isValidLinkUrl(v.url);
  const set = (part, val) => {
    const next = { ...v, [part]: val };
    setV(next);
    if (next.url && next.url.trim() && !isValidLinkUrl(next.url)) return;
    onChange(next.url || next.text ? next : null);
  };
  return (
    <>
      <div className="spt-field-label">URL</div>
      <TextField size={TextField.sizes.SMALL} placeholder="https://" value={v.url || ''} onChange={(val) => set('url', val)} autoFocus />
      {badUrl && (
        <p className="mt-1 text-xs text-danger-ink" role="alert">
          Enter a web address, like example.com.
        </p>
      )}
      <div className="spt-field-label">Text to display</div>
      <TextField size={TextField.sizes.SMALL} value={v.text || ''} onChange={(val) => set('text', val)} />
    </>
  );
}

/**
 * Rating: one clickable symbol per step, using THIS column's symbol, count and
 * colour from its board settings. Clicking the current rating clears it, which
 * is how monday's own rating cell behaves.
 */
function RatingEditor({ column, value, onChange, onClose }) {
  const { limit, symbol, color } = ratingSettings(column);
  return (
    <>
      <div className="spt-ratingpicker">
        {Array.from({ length: limit }, (_, i) => {
          const step = i + 1;
          const filled = !!value && step <= value;
          return (
            <button
              key={step}
              type="button"
              className="spt-ratingbtn"
              aria-label={`${step} of ${limit}`}
              onClick={() => {
                onChange(value === step ? null : step);
                onClose();
              }}
            >
              <RatingSymbol symbol={symbol} color={color} filled={filled} size={22} />
            </button>
          );
        })}
      </div>
      <Button
        kind={Button.kinds.TERTIARY}
        size={Button.sizes.SMALL}
        leftIcon={CloseSmall}
        onClick={() => {
          onChange(null);
          onClose();
        }}
      >
        Clear
      </Button>
    </>
  );
}

/**
 * Checkbox cells normally toggle straight from the grid (TemplateEditor
 * intercepts the click), so this exists as a correct fallback rather than
 * letting a checkbox fall through to a text box.
 */
function CheckboxEditor({ value, onChange, onClose }) {
  return (
    <div className="spt-menu-list">
      <div className="spt-menu-row spt-menu-row-check">
        <Checkbox
          checked={!!value}
          onChange={() => {
            onChange(!value ? true : null);
            onClose();
          }}
          label="Checked"
        />
      </div>
    </div>
  );
}

/** Text and numbers: the cell follows each keystroke; Enter closes the editor. */
function TextEditor({ column, value, onChange, onClose }) {
  return (
    <TextField
      type={canonicalType(column.type) === 'numbers' ? 'number' : 'text'}
      size={TextField.sizes.SMALL}
      inputAriaLabel={column.title}
      value={value === null || value === undefined ? '' : String(value)}
      onChange={(v) => onChange(v === '' ? null : v)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onClose();
      }}
      autoFocus
    />
  );
}

// Types whose picker is built from the board's column settings — labels, options,
// rating scale. Every other type edits from the stored value alone.
const NEEDS_BOARD_SETTINGS = new Set(['status', 'dropdown', 'rating']);

export default function CellEditor({
  anchorEl,
  column,
  boardLoading, // the board (and so the column's settings) is still being read
  boardError, // the board could not be read
  users,
  usersError,
  usersLoading,
  onNeedUsers,
  files, // lib/files service (upload, remove, usage, downloadUrl) for File columns
  uploads, // the editor's lib/cellUploads store, for File columns
  cell, // this cell as the editor describes it to `uploads`
  value,
  onChange,
  onClose,
}) {
  const kind = canonicalType(column.type);
  let body;
  // A picker opened before the board arrives would show "not on the current
  // board" for a column that IS on it — wait instead. Once the board is in, a
  // column with no settings really is unmatched, and the editors say so.
  if (NEEDS_BOARD_SETTINGS.has(kind) && !column.settings && (boardLoading || boardError)) {
    body = boardLoading ? (
      <div className="spt-menu-empty flex items-center gap-2" role="status">
        <Loader2 className="w-4 h-4 flex-none animate-spin" aria-hidden="true" />
        Reading your board columns and column settings
      </div>
    ) : (
      <div className="spt-menu-empty" role="alert">
        Could not read this board's subitem columns, so this column's choices are unavailable.
        Refresh to try again.
      </div>
    );
    return (
      <AnchoredPopover anchorEl={anchorEl} onClose={onClose} width={230}>
        {body}
      </AnchoredPopover>
    );
  }
  switch (kind) {
    case 'status':
      body = <StatusEditor column={column} value={value} onChange={onChange} onClose={onClose} />;
      break;
    case 'people':
      body = (
        <PeopleEditor
          users={users}
          usersError={usersError}
          usersLoading={usersLoading}
          onNeedUsers={onNeedUsers}
          value={value}
          onChange={onChange}
        />
      );
      break;
    case 'dropdown':
      body = <DropdownEditor column={column} value={value} onChange={onChange} />;
      break;
    case 'date':
      body = <DateEditor value={value} onChange={onChange} onClose={onClose} />;
      break;
    case 'timeline':
      body = <TimerangeEditor value={value} onChange={onChange} onClose={onClose} />;
      break;
    case 'link':
      body = <LinkEditor value={value} onChange={onChange} />;
      break;
    case 'checkbox':
      body = <CheckboxEditor value={value} onChange={onChange} onClose={onClose} />;
      break;
    case 'rating':
      body = <RatingEditor column={column} value={value} onChange={onChange} onClose={onClose} />;
      break;
    case 'file':
      body = <FileCellEditor files={files} uploads={uploads} cell={cell} value={value} onChange={onChange} />;
      break;
    default:
      body = <TextEditor column={column} value={value} onChange={onChange} onClose={onClose} />;
  }

  // AnchoredPopover positions with arithmetic, so width must stay numeric.
  // Rating widens with the column's own symbol count.
  const width =
    kind === 'people'
      ? 260
      : kind === 'file'
        ? 320 // a file name, its size and two buttons per row
      : kind === 'rating'
        ? Math.max(180, 48 + ratingSettings(column).limit * 30)
        : kind === 'date'
          ? 340 // the Dynamic tab's "( ) Today [+n] [working days]" row
          : kind === 'timeline'
            ? 440 // the Dynamic tab's "End [Start] [+n] [working days]" row, indented under Custom
            : 230;
  return (
    <AnchoredPopover anchorEl={anchorEl} onClose={onClose} width={width}>
      {body}
    </AnchoredPopover>
  );
}
